import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, Loader2, Check, ShoppingCart, RefreshCw, Info, Lock, Unlock, Sparkles, MessageSquare, Layers, Laptop } from "lucide-react";
import Navbar from "../components/Navbar";
import StickerPreview from "../components/StickerPreview";
import FaqSection from "../components/FaqSection";
import MaterialDescription from "../components/MaterialDescription";
import Seo from "../components/Seo";
import { finishBySlug, productJsonLd } from "../lib/finishes";
import { ShippingCountdown } from "../components/ShippingCountdown";
import { api, fileUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useI18n, formatCAD } from "../i18n";
import { nestSheet } from "../lib/nesting";

const MATERIAL_SWATCH = {
  white_vinyl: "bg-white",
  transparent_vinyl: "checkerboard",
  chrome_vinyl: "chrome-swatch",
  holographic_vinyl: "bg-gradient-to-br from-nbcyan via-nbpink to-nbyellow",
};

const round2 = (n) => Math.round(Number(n) * 100) / 100;

export default function Designer() {
  const { t } = useI18n();
  const [config, setConfig] = useState(null);
  const [sticker, setSticker] = useState(null); // {id, original_path, preview_path, aspect}
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [material, setMaterial] = useState("white_vinyl");
  const [cutType, setCutType] = useState("die_cut");
  const [width, setWidth] = useState(3);
  const [height, setHeight] = useState(3);
  const [lockAspect, setLockAspect] = useState(true);
  const [showMoreSizes, setShowMoreSizes] = useState(false);
  const [showMoreQty, setShowMoreQty] = useState(false);
  const [quantity, setQuantity] = useState(50);
  const [message, setMessage] = useState("");
  const [shape, setShape] = useState("contour_cut");
  const [offset, setOffset] = useState(0.125);
  const [upsellDismissed, setUpsellDismissed] = useState(false);
  const [quote, setQuote] = useState(null);
  const [pricing, setPricing] = useState(false);
  const [viewMode, setViewMode] = useState("surface");
  const [mockup, setMockup] = useState("laptop");
  const [renderV, setRenderV] = useState("");
  const [content, setContent] = useState(null);
  const rerenderRef = useRef({ ratio: 0.06 });

  const fileRef = useRef(null);
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeParams = useParams();
  const routeFinish = finishBySlug(routeParams.finish);

  useEffect(() => {
    api.get("/pricing/config").then(({ data }) => {
      setConfig(data);
      const keys = (data.materials || []).map((m) => m.key);
      const finish = routeFinish?.key || searchParams.get("finish");
      if (finish && keys.includes(finish)) {
        setMaterial(finish);
      } else if (data.materials?.[0]) {
        setMaterial(data.materials[0].key);
      }
    });
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
  }, [searchParams, routeFinish]);

  // Load a saved design when arriving via /design?design=<id>.
  useEffect(() => {
    const id = searchParams.get("design");
    if (!id) return;
    api.get(`/stickers/${id}`).then(({ data }) => {
      setSticker(data);
      setRenderV("");
      rerenderRef.current = { ratio: 0.06 };
      if (data.aspect) {
        const step = 3;
        if (data.aspect >= 1) { setWidth(step); setHeight(round2(step / data.aspect)); }
        else { setHeight(step); setWidth(round2(step * data.aspect)); }
      }
    }).catch(() => toast.error("Could not load that design."));
  }, [searchParams]);

  const fetchQuote = useCallback(async () => {
    if (!width || !height || !quantity) return;
    setPricing(true);
    try {
      const { data } = await api.post("/price/quote", {
        width_in: Number(width),
        height_in: Number(height),
        material_key: material,
        cut_type: cutType,
        quantity: Number(quantity),
        shape,
        offset_in: Number(offset),
      });
      setQuote(data);
    } catch (e) {
      setQuote(null);
    } finally {
      setPricing(false);
    }
  }, [width, height, material, cutType, quantity, shape, offset]);

  useEffect(() => {
    const t = setTimeout(fetchQuote, 350);
    return () => clearTimeout(t);
  }, [fetchQuote]);

  // The white offset (cut border) is customer-selectable. Regenerate the baked
  // preview whenever the offset (as a fraction of the longest side) shifts.
  useEffect(() => {
    if (!sticker?.id || !width || !height) return;
    const maxDim = Math.max(Number(width), Number(height)) || 1;
    const desired = Math.min(Number(offset) / maxDim, 0.45);
    if (Math.abs(desired - rerenderRef.current.ratio) < 0.002) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/stickers/${sticker.id}/rerender`, {
          width_in: Number(width),
          height_in: Number(height),
          offset_in: Number(offset),
        });
        rerenderRef.current = { ratio: desired };
        setRenderV(data.v);
      } catch (e) {
        /* keep previous preview on failure */
      }
    }, 450);
    return () => clearTimeout(t);
  }, [sticker, width, height, offset]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.(png|webp)$/i.test(file.name)) {
      toast.error("Please upload a PNG file (transparent background works best).");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/stickers/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSticker(data);
      setRenderV("");
      rerenderRef.current = { ratio: 0.06 };
      // Snap to a sensible default preset that respects the detected aspect ratio.
      if (data.aspect) {
        const step = 3;
        if (data.aspect >= 1) {
          setWidth(step);
          setHeight(round2(step / data.aspect));
        } else {
          setHeight(step);
          setWidth(round2(step * data.aspect));
        }
      }
      toast.success("Sticker traced! Contour cut-line generated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed. Try another PNG.");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const aspect = sticker?.aspect || 1;
  const isSheet = cutType === "sheet_cut";

  // Let a dismissed upsell reappear when the customer changes the configuration.
  useEffect(() => {
    setUpsellDismissed(false);
  }, [width, height, material, cutType, quantity, shape, offset]);
  // The selected width/height IS the final sticker footprint (vinyl incl. cut border).
  const footW = Number(width) || 0;
  const footH = Number(height) || 0;
  const nest = useMemo(() => nestSheet(footW, footH, config?.sheet_gap_in), [footW, footH, config]);

  // Print-quality caution: warn when artwork DPI at the selected size is under 150.
  const lowResDpi = useMemo(() => {
    if (!sticker?.content_w || !quote?.artwork_width || !quote?.artwork_height) return null;
    const dpi = Math.min(sticker.content_w / quote.artwork_width, sticker.content_h / quote.artwork_height);
    return dpi < 150 ? Math.round(dpi) : null;
  }, [sticker, quote]);

  // Preset "longest-side" steps derived from admin-defined sizes.
  const sizeSteps = useMemo(() => {
    if (!config) return { base: [], all: [] };
    const admin = config.sizes.map((s) => Math.max(s.width_in, s.height_in));
    const extended = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
    const base = [...new Set(admin)].sort((a, b) => a - b);
    const all = [...new Set([...admin, ...extended])].sort((a, b) => a - b);
    return { base, all };
  }, [config]);

  const shownSteps = showMoreSizes ? sizeSteps.all : sizeSteps.base;

  // Customer-facing quantity presets come from admin config (production-v1).
  const qtyOpts = useMemo(() => {
    const enabled = (config?.quantity_options || []).filter((o) => o.enabled).map((o) => o.qty).sort((a, b) => a - b);
    return enabled.length ? enabled : [10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000];
  }, [config]);

  const dimsForStep = useCallback(
    (step) => (aspect >= 1 ? { w: step, h: round2(step / aspect) } : { w: round2(step * aspect), h: step }),
    [aspect]
  );

  const applyPreset = (step) => {
    const d = dimsForStep(step);
    setWidth(d.w);
    setHeight(d.h);
  };

  const onWidth = (v) => {
    setWidth(v);
    if (lockAspect && sticker && v) setHeight(round2(Number(v) / aspect));
  };
  const onHeight = (v) => {
    setHeight(v);
    if (lockAspect && sticker && v) setWidth(round2(Number(v) * aspect));
  };

  // Best-value upsell comes from the backend engine (evaluates every enabled
  // larger quantity independently and returns the lowest-additional-cost upgrade).
  const upsell = quote?.upsell || null;

  const addToCart = () => {
    if (!sticker) {
      toast.error("Upload artwork first.");
      return;
    }
    if (!quote) return;
    if (isSheet && (!quote.per_sheet || quote.per_sheet < 1)) {
      toast.error("Sticker too large for a sheet — try Kiss Cut.");
      return;
    }
    addItem({
      sticker_id: sticker.id,
      preview_path: sticker.preview_path,
      original_path: sticker.original_path,
      holo_path: sticker.holo_path,
      aspect: sticker.aspect,
      name: "Custom Sticker",
      width_in: Number(width),
      height_in: Number(height),
      material_key: material,
      material_name: quote.material_name,
      cut_type: cutType,
      cut_name: quote.cut_name,
      shape,
      offset_in: Number(offset),
      quantity: Number(quantity),
      per_sheet: quote.per_sheet,
      total_stickers: quote.total_stickers,
      message: message.trim(),
      unit_price: quote.unit_price,
      line_total: quote.total,
    });
    toast.success(t("designer.added"));
    setMessage("");
  };

  if (!config) {
    return (
      <div className="App">
        <Navbar />
        <div className="flex justify-center py-32">
          <Loader2 className="animate-spin" size={40} />
        </div>
      </div>
    );
  }

  const seo = routeFinish
    ? { path: `/stickers/${routeFinish.slug}`, title: routeFinish.title, description: routeFinish.description, image: routeFinish.ogImage, type: "product", jsonLd: productJsonLd(routeFinish) }
    : { path: "/design", title: t("designer.seoTitle"), description: t("designer.seoDesc") };

  return (
    <div className="App">
      <Seo path={seo.path} title={seo.title} description={seo.description} image={seo.image} type={seo.type} jsonLd={seo.jsonLd} />
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <ShippingCountdown />
        <h1 className="font-head font-extrabold text-4xl sm:text-5xl mb-2">{t("designer.title")}</h1>
        <p className="text-nbblack/60 mb-8 text-lg">{t("designer.subtitle")}</p>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Preview (8 cols) */}
          <div className="lg:col-span-8">
            <div className="bg-white nb-border nb-shadow-lg rounded-3xl p-4 sm:p-6">
              {sticker && (
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex rounded-full nb-border overflow-hidden font-head font-bold text-sm">
                    <button
                      data-testid="view-surface"
                      onClick={() => setViewMode("surface")}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 ${viewMode === "surface" ? "bg-nbblack text-white" : "bg-white"}`}
                    >
                      <Layers size={15} /> {t("designer.surface")}
                    </button>
                    <button
                      data-testid="view-context"
                      onClick={() => setViewMode("context")}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 border-l-[3px] border-nbblack ${viewMode === "context" ? "bg-nbblack text-white" : "bg-white"}`}
                    >
                      <Laptop size={15} /> {t("designer.inContext")}
                    </button>
                  </div>

                  {viewMode === "context" && (
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { k: "laptop", l: t("designer.laptop") },
                        { k: "notebook", l: t("designer.notebook") },
                        { k: "bottle", l: t("designer.bottle") },
                        { k: "phone", l: t("designer.phone") },
                      ].map((m) => (
                        <button
                          key={m.k}
                          data-testid={`mockup-${m.k}`}
                          onClick={() => setMockup(m.k)}
                          className={`px-3 py-1.5 rounded-full nb-border font-head font-bold text-xs nb-press ${mockup === m.k ? "bg-nbpink text-white" : "bg-white"}`}
                        >
                          {m.l}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {sticker ? (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={material + cutType + viewMode + mockup}
                    initial={{ opacity: 0.4 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <StickerPreview
                      originalUrl={fileUrl(sticker.original_path)}
                      previewUrl={fileUrl(sticker.preview_path) + (renderV ? `?v=${renderV}` : "")}
                      holoUrl={sticker.holo_path ? fileUrl(sticker.holo_path) + (renderV ? `?v=${renderV}` : "") : null}
                      material={material}
                      cutType={cutType}
                      aspect={sticker.aspect || 1}
                      viewMode={viewMode}
                      mockup={mockup}
                      widthIn={footW}
                      heightIn={footH}
                      offsetIn={offset}
                      sheetGap={config?.sheet_gap_in}
                    />
                  </motion.div>
                </AnimatePresence>
              ) : (
                <div
                  data-testid="upload-zone"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  className={`min-h-[420px] rounded-2xl border-4 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors ${
                    dragOver ? "border-nbpink bg-nbpink/5" : "border-nbblack/25 hover:border-nbpink"
                  }`}
                >
                  {uploading ? (
                    <>
                      <Loader2 size={56} className="animate-spin text-nbpink mb-4" />
                      <p className="font-head font-bold text-xl">{t("designer.tracing")}</p>
                    </>
                  ) : (
                    <>
                      <div className="w-20 h-20 rounded-full bg-nbyellow nb-border nb-shadow flex items-center justify-center mb-5">
                        <UploadCloud size={40} strokeWidth={2.2} />
                      </div>
                      <p className="font-head font-extrabold text-2xl mb-1">{t("designer.dropHere")}</p>
                      <p className="text-nbblack/50">{t("designer.browse")}</p>
                    </>
                  )}
                </div>
              )}
              <input ref={fileRef} type="file" accept=".png,.webp" hidden onChange={(e) => handleFile(e.target.files?.[0])} data-testid="file-input" />

              {sticker && (
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="replace-image"
                  className="mt-4 inline-flex items-center gap-2 font-head font-bold px-5 py-2.5 rounded-full nb-border nb-shadow-sm nb-press bg-white"
                >
                  <RefreshCw size={16} /> {t("designer.replace")}
                </button>
              )}
            </div>

            {content?.material_descriptions?.[material] && (
              <div className="mt-6">
                <MaterialDescription desc={content.material_descriptions[material]} />
              </div>
            )}

            {content && (
              <FaqSection
                items={content.faq_designer}
                title={t("designer.faqTitle")}
                jsonLdId="designer-faq-jsonld"
                testid="designer-faq"
                className="!py-8"
                contained={false}
              />
            )}
          </div>

          {/* Controls (4 cols) */}
          <div className="lg:col-span-4 space-y-5">
            {lowResDpi && (
              <div className="bg-nbyellow bg-[#FFF3C4] nb-border nb-shadow-sm rounded-2xl p-4 flex items-start gap-3" data-testid="lowres-warning">
                <Info size={20} className="shrink-0 text-orange-600 mt-0.5" />
                <p className="text-sm font-semibold text-nbblack/80">
                  {t("designer.lowResWarn")} <b>~{lowResDpi} DPI</b> {t("designer.lowResTail")}
                </p>
              </div>
            )}
            {/* Material */}
            <div className="bg-[#FFE3F1] nb-border nb-shadow rounded-2xl p-5">
              <h3 className="font-head font-extrabold text-lg mb-3">{t("designer.material")}</h3>
              <div className="space-y-2.5">
                {config.materials.map((m) => (
                  <button
                    key={m.key}
                    data-testid={`material-${m.key}`}
                    onClick={() => setMaterial(m.key)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl nb-border text-left nb-press ${
                      material === m.key ? "bg-nbyellow nb-shadow-sm" : "bg-white"
                    }`}
                  >
                    <span className={`w-11 h-11 rounded-lg nb-border shrink-0 ${MATERIAL_SWATCH[m.key] || "bg-white"}`} />
                    <span className="flex-1 font-head font-bold">{m.name}</span>
                    {material === m.key && <Check size={18} />}
                  </button>
                ))}
              </div>
            </div>

            {/* Sticker offset slider */}
            <div className="bg-[#FFE3F1] nb-border nb-shadow rounded-2xl p-5" data-testid="offset-card">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-head font-extrabold text-lg">{t("designer.offsetTitle")}</h3>
                <span className="font-head font-bold text-sm bg-white nb-border rounded-full px-3 py-0.5" data-testid="offset-value">
                  {offset.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")}"
                </span>
              </div>
              <input
                data-testid="offset-slider"
                type="range"
                min="0.0625"
                max="0.5"
                step="0.0625"
                value={offset}
                onChange={(e) => setOffset(Number(e.target.value))}
                className="w-full accent-nbpink cursor-pointer"
              />
              <div className="flex justify-between text-[11px] font-semibold text-nbblack/50 mt-1">
                <span>1/16"</span>
                <span>1/2"</span>
              </div>
              <p className="text-[11px] text-nbblack/60 mt-2">{t("designer.offsetHint")}</p>
            </div>

            {/* Cut type */}
            <div className="bg-[#DBF9FF] nb-border nb-shadow rounded-2xl p-5">
              <h3 className="font-head font-extrabold text-lg mb-3">{t("designer.cutType")}</h3>
              <div className="grid grid-cols-3 gap-2">
                {config.cut_types.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`cut-${c.key}`}
                    onClick={() => setCutType(c.key)}
                    className={`p-2.5 rounded-xl nb-border nb-press text-left ${cutType === c.key ? "bg-nbcyan nb-shadow-sm" : "bg-white"}`}
                  >
                    <p className="font-head font-bold text-sm leading-tight">{c.name}</p>
                    <p className="text-[11px] text-nbblack/60 mt-0.5 leading-tight">
                      {c.key === "kiss_cut" ? t("designer.kissDesc") : c.key === "sheet_cut" ? t("designer.sheetDesc") : t("designer.dieDesc")}
                    </p>
                  </button>
                ))}
              </div>
              {cutType === "sheet_cut" && (
                <div className="mt-3 text-xs font-semibold text-nbblack/70 bg-white/70 rounded-xl nb-border p-2.5" data-testid="sheet-info">
                  {nest.n >= 1 ? (
                    <>
                      <b>{nest.n}</b> {nest.n > 1 ? t("designer.nestStickers") : t("designer.nestSticker")} {t("designer.nestPre")}
                      {nest.n === 1 && (
                        <span className="block mt-1 text-nbpink">{t("designer.only1")} <button data-testid="suggest-kiss" onClick={() => setCutType("kiss_cut")} className="underline font-bold">{t("designer.useKissInstead")}</button>.</span>
                      )}
                    </>
                  ) : (
                    <span className="text-nbpink">{t("designer.tooLarge")} <button onClick={() => setCutType("kiss_cut")} className="underline font-bold">{t("designer.useKiss")}</button>.</span>
                  )}
                </div>
              )}
            </div>

            {/* Shape */}
            <div className="bg-[#E7FFE9] nb-border nb-shadow rounded-2xl p-5">
              <h3 className="font-head font-extrabold text-lg mb-3">{t("designer.shapeTitle")}</h3>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { k: "contour_cut", l: t("designer.shapeContour") },
                  { k: "rectangle", l: t("designer.shapeRect") },
                  { k: "circle", l: t("designer.shapeCircle") },
                ].map((s) => (
                  <button
                    key={s.k}
                    data-testid={`shape-${s.k}`}
                    onClick={() => setShape(s.k)}
                    className={`p-2.5 rounded-xl nb-border nb-press text-sm font-head font-bold leading-tight ${shape === s.k ? "bg-nbcyan nb-shadow-sm" : "bg-white"}`}
                  >
                    {s.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div className="bg-[#FFF8CC] nb-border nb-shadow rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-head font-extrabold text-lg">{t("designer.printedSize")}</h3>
                <button
                  data-testid="lock-aspect"
                  onClick={() => setLockAspect((v) => !v)}
                  className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full nb-border nb-press ${
                    lockAspect ? "bg-nbblack text-white" : "bg-white"
                  }`}
                  title="Keep the sticker's aspect ratio"
                >
                  {lockAspect ? <Lock size={13} /> : <Unlock size={13} />}
                  {lockAspect ? t("designer.ratioLocked") : t("designer.freeRatio")}
                </button>
              </div>

              {sticker && (
                <p className="text-xs font-semibold text-nbblack/50 mb-3">
                  {t("designer.presetsNote")} ({aspect >= 1 ? `${aspect.toFixed(2)} : 1` : `1 : ${(1 / aspect).toFixed(2)}`}).
                </p>
              )}

              <div className="flex flex-wrap gap-2 mb-3">
                {shownSteps.map((step) => {
                  const d = dimsForStep(step);
                  const active = Number(width) === d.w && Number(height) === d.h;
                  return (
                    <button
                      key={step}
                      data-testid={`size-${step}`}
                      onClick={() => applyPreset(step)}
                      className={`flex flex-col items-center px-3.5 py-2 rounded-2xl nb-border font-head font-bold text-sm nb-press leading-tight ${
                        active ? "bg-nbpink text-white" : "bg-white"
                      }`}
                    >
                      <span className="block">{step}"</span>
                      <span className={`block text-[10px] font-semibold mt-0.5 ${active ? "text-white/80" : "text-nbblack/50"}`}>
                        {d.w}" × {d.h}"
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                data-testid="toggle-more-sizes"
                onClick={() => setShowMoreSizes((v) => !v)}
                className="text-xs font-head font-bold underline underline-offset-2 text-nbblack/70 hover:text-nbpink mb-4"
              >
                {showMoreSizes ? t("designer.showFewerSizes") : t("designer.viewMoreSizes")}
              </button>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-nbblack/60">{t("designer.width")}</label>
                  <input
                    data-testid="input-width"
                    type="number"
                    min="1"
                    step="0.5"
                    value={width}
                    onChange={(e) => onWidth(e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-xl nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white font-bold"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-nbblack/60">{t("designer.height")}</label>
                  <input
                    data-testid="input-height"
                    type="number"
                    min="1"
                    step="0.5"
                    value={height}
                    onChange={(e) => onHeight(e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-xl nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white font-bold"
                  />
                </div>
              </div>
            </div>

            {/* Quantity */}
            <div className="bg-[#EDE3FF] nb-border nb-shadow rounded-2xl p-5">
              <h3 className="font-head font-extrabold text-lg mb-3">{isSheet ? t("designer.sheets") : t("designer.quantity")}</h3>
              <div className="flex flex-wrap gap-2">
                {(isSheet
                  ? (showMoreQty ? [1, 5, 10, 20, 50, 100, 200, 500] : [1, 5, 10, 20, 50])
                  : (showMoreQty ? qtyOpts : qtyOpts.slice(0, 5))
                ).map((q) => (
                  <button
                    key={q}
                    data-testid={`qty-${q}`}
                    onClick={() => setQuantity(q)}
                    className={`px-3.5 py-1.5 rounded-full nb-border font-head font-bold text-sm nb-press ${
                      Number(quantity) === q ? "bg-nbpink text-white" : "bg-white"
                    }`}
                  >
                    {q}
                  </button>
                ))}
              </div>
              <button
                data-testid="toggle-more-qty"
                onClick={() => setShowMoreQty((v) => !v)}
                className="mt-2 text-xs font-head font-bold underline underline-offset-2 text-nbblack/70 hover:text-nbpink"
              >
                {showMoreQty ? t("designer.showFewer") : t("designer.showMore")}
              </button>
              <input
                data-testid="input-quantity"
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full mt-3 px-3 py-2 rounded-xl nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white font-bold"
              />

              {isSheet && quote?.per_sheet >= 1 && (
                <p className="mt-3 text-xs font-semibold text-nbblack/70" data-testid="sheet-total">
                  {quantity} {Number(quantity) > 1 ? t("designer.sheetsWord") : t("designer.sheetWord")} × {quote.per_sheet} = <b>{quote.total_stickers} {t("designer.stickersTotal")}</b> {t("designer.totalWord")}
                </p>
              )}

              <AnimatePresence>
                {upsell && !upsellDismissed && (
                  <motion.div
                    key={upsell.quantity}
                    initial={{ opacity: 0, y: 8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6 }}
                    data-testid="upsell-banner"
                    className="mt-4 bg-nbyellow nb-border nb-shadow-sm rounded-2xl p-4"
                  >
                    <div className="flex items-center gap-2 font-head font-extrabold text-sm mb-1">
                      <Sparkles size={16} /> {t("designer.upTitle")}
                    </div>
                    <p className="text-xs font-semibold text-nbblack/80">
                      {t("designer.upGetA")} <b>{upsell.additional_quantity}</b> {t("designer.upMoreItems")}{" "}
                      {isSheet ? t("designer.upSheetsWord") : t("designer.upStickersWord")} {t("designer.upForOnly")}{" "}
                      <b>{formatCAD(upsell.additional_cost)}</b> {t("designer.upMoreCost")}
                    </p>
                    <p className="text-xs font-semibold text-nbblack/70 mt-1">
                      {t("designer.upFromWord")} <b>{upsell.current_quantity}</b> {t("designer.upToWord")}{" "}
                      <b>{upsell.quantity}</b> {t("designer.upSaveWord")} <b>{upsell.unit_savings_pct}%</b>{" "}
                      {isSheet ? t("designer.upPerSheet") : t("designer.upPerSticker")}.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button
                        data-testid="upsell-accept"
                        onClick={() => setQuantity(upsell.quantity)}
                        className="flex-1 bg-nbblack text-white font-head font-bold text-sm py-2 rounded-full nb-border nb-press"
                      >
                        {t("designer.upBtn")} {upsell.quantity}
                      </button>
                      <button
                        data-testid="upsell-dismiss"
                        onClick={() => setUpsellDismissed(true)}
                        className="px-4 bg-white font-head font-bold text-sm py-2 rounded-full nb-border nb-press"
                      >
                        {t("designer.upKeep")} {upsell.current_quantity}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Message */}
            <div className="bg-white nb-border nb-shadow rounded-2xl p-5">
              <h3 className="font-head font-extrabold text-lg mb-3 flex items-center gap-2">
                <MessageSquare size={18} /> {t("designer.addMessage")}
              </h3>
              <textarea
                data-testid="input-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder={t("designer.msgPlaceholder")}
                className="w-full px-3 py-2 rounded-xl nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white font-semibold text-sm resize-none"
              />
              <p className="text-right text-[11px] font-semibold text-nbblack/40 mt-1">{message.length}/500</p>
            </div>

            {/* Price + CTA */}
            <div className="bg-nbblack text-white nb-border nb-shadow-lg rounded-2xl p-5">
              <div className="flex items-end justify-between mb-1">
                <span className="font-head font-bold text-white/70">{t("designer.total")}</span>
                <span data-testid="price-total" className="font-head font-extrabold text-4xl">
                  {pricing ? "…" : quote ? formatCAD(quote.total) : "—"}
                </span>
              </div>
              {quote && (
                <div className="flex items-center justify-between text-sm text-white/60 mb-4">
                  <span>
                    {formatCAD(quote.unit_price)} {t("designer.perSticker")}
                    {isSheet && quote.price_per_sheet ? ` · ${formatCAD(quote.price_per_sheet)}${t("designer.perSheet")}` : ""}
                  </span>
                  {quote.discount_pct > 0 && (
                    <span className="bg-nbyellow text-nbblack font-bold px-2 py-0.5 rounded-full text-xs">
                      {quote.discount_pct}% {t("designer.bulkOff")}
                    </span>
                  )}
                </div>
              )}
              <button
                data-testid="add-to-cart"
                onClick={addToCart}
                disabled={!sticker || !quote}
                className="w-full bg-nbpink text-white font-head font-bold text-lg py-3 rounded-full nb-border nb-press disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <ShoppingCart size={20} /> {t("designer.addToCart")}
              </button>
              <p className="text-xs text-white/40 mt-3 flex items-center gap-1.5">
                <Info size={13} /> {t("designer.finalPrice")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
